/**
 * Copyright (c) 2020 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author David Sehnal <david.sehnal@gmail.com>
 */
import { StructureElement, StructureProperties as P } from '../../structure';
import { Segmentation } from '../../../../mol-data/int';
import { CifWriter } from '../../../../mol-io/writer/cif';
import { Column } from '../../../../mol-data/db';
export function atom_site_operator_mapping(ctx) {
  var entries = getEntries(ctx);
  if (entries.length === 0) return;
  return [Category, entries, {
    ignoreFilter: true
  }];
}
export var AtomSiteOperatorMappingCategoryName = 'molstar_atom_site_operator_mapping';
export var AtomSiteOperatorMappingSchema = {
  molstar_atom_site_operator_mapping: {
    label_asym_id: Column.Schema.Str(),
    auth_asym_id: Column.Schema.Str(),
    operator_name: Column.Schema.Str(),
    suffix: Column.Schema.Str(),
    // assembly
    assembly_id: Column.Schema.Str(),
    assembly_operator_id: Column.Schema.Int(),
    // symmetry
    symmetry_operator_index: Column.Schema.Int(),
    symmetry_hkl: Column.Schema.Vector(3),
    // NCS
    ncs_id: Column.Schema.Int()
  }
};

var asmValueKind = function (i, xs) {
  return typeof xs[i].operator.assembly === 'undefined' ? 1
  /* NotPresent */
  : 0
  /* Present */
  ;
};

var symmetryValueKind = function (i, xs) {
  return xs[i].operator.spgrOp === -1 ? 1
  /* NotPresent */
  : 0
  /* Present */
  ;
};

var Fields = CifWriter.fields().str('label_asym_id', function (i, xs) {
  return xs[i].label_asym_id;
}).str('auth_asym_id', function (i, xs) {
  return xs[i].auth_asym_id;
}).str('operator_name', function (i, xs) {
  return xs[i].operator.name;
}).str('suffix', function (i, xs) {
  return xs[i].operator.suffix;
}) // assembly
// TODO: include oper list as well?
.str('assembly_id', function (i, xs) {
  var _a;

  return ((_a = xs[i].operator.assembly) === null || _a === void 0 ? void 0 : _a.id) || '';
}, {
  valueKind: asmValueKind
}).int('assembly_operator_id', function (i, xs) {
  var _a;

  return ((_a = xs[i].operator.assembly) === null || _a === void 0 ? void 0 : _a.operId) || 0;
}, {
  valueKind: asmValueKind
}) // symmetry
.int('symmetry_operator_index', function (i, xs) {
  return xs[i].operator.spgrOp;
}, {
  valueKind: symmetryValueKind
}).vec('symmetry_hkl', [function (i, xs) {
  return xs[i].operator.hkl[0];
}, function (i, xs) {
  return xs[i].operator.hkl[1];
}, function (i, xs) {
  return xs[i].operator.hkl[2];
}], {
  valueKind: symmetryValueKind
}) // NCS
.int('ncs_id', function (i, xs) {
  return xs[i].operator.ncsId;
}, {
  valueKind: symmetryValueKind
}).getFields();
var Category = {
  name: 'molstar_atom_site_operator_mapping',
  instance: function (entries) {
    return {
      fields: Fields,
      source: [{
        data: entries,
        rowCount: entries.length
      }]
    };
  }
};

function getEntries(ctx) {
  var existing = new Set();
  var entries = [];

  for (var _i = 0, _a = ctx.structures; _i < _a.length; _i++) {
    var s = _a[_i];
    var l = StructureElement.Location.create(s);

    for (var _b = 0, _c = s.units; _b < _c.length; _b++) {
      var unit = _c[_b];
      var operator = unit.conformation.operator;
      if (!operator.suffix || unit.kind !== 0
      /* Atomic */
      ) continue;
      l.unit = unit;
      var elements = unit.elements;
      var chainsIt = Segmentation.transientSegments(unit.model.atomicHierarchy.chainAtomSegments, elements);

      while (chainsIt.hasNext) {
        var chainSegment = chainsIt.move();
        l.element = elements[chainSegment.start];
        var label_asym_id = P.chain.label_asym_id(l);
        var key = "" + label_asym_id + operator.suffix;
        if (existing.has(key)) continue;
        existing.add(key);
        var auth_asym_id = P.chain.label_asym_id(l);
        entries.push({
          label_asym_id: label_asym_id,
          auth_asym_id: auth_asym_id,
          operator: operator
        });
      }
    }
  }

  return entries;
}